import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { referralAPI } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Users,
  Gift,
  DollarSign,
  Trophy,
  Copy,
  Check,
  Share2,
  QrCode,
  TrendingUp,
  Clock,
  CreditCard,
  Wallet,
  Award,
  Crown,
  Loader2,
  ExternalLink,
  Mail,
  Twitter,
  Facebook,
  MessageCircle,
} from "lucide-react";
import QRCode from "qrcode";
import BadgesDisplay from "../components/BadgesDisplay";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const TIER_COLORS = {
  bronze: "from-amber-700 to-amber-500",
  silver: "from-slate-400 to-slate-300",
  gold: "from-yellow-500 to-amber-400",
};

const TIER_BADGES = {
  bronze: { icon: Award, color: "text-amber-600" },
  silver: { icon: Award, color: "text-slate-400" },
  gold: { icon: Crown, color: "text-yellow-500" },
};

export default function ReferralDashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [copied, setCopied] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [payoutForm, setPayoutForm] = useState({
    amount: "",
    method: "credits",
    paypalEmail: "",
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, historyRes, leaderboardRes] = await Promise.all([
        referralAPI.getMyStats(),
        referralAPI.getHistory(),
        referralAPI.getLeaderboard(),
      ]);
      
      setStats(statsRes.data);
      setHistory(historyRes.data.referrals || []);
      setLeaderboard(leaderboardRes.data.leaderboard || []);
      
      // Generate QR code
      if (statsRes.data.referral_link) {
        const qr = await QRCode.toDataURL(statsRes.data.referral_link, {
          width: 200,
          margin: 2,
          color: { dark: "#000000", light: "#ffffff" },
        });
        setQrCodeUrl(qr);
      }
    } catch (error) {
      console.error("Failed to load referral data:", error);
      toast.error("Failed to load referral data");
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    if (stats?.referral_link) {
      navigator.clipboard.writeText(stats.referral_link);
      setCopied(true);
      toast.success("Referral link copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareOnSocial = (platform) => {
    const message = encodeURIComponent(
      `Create viral videos with AI! Use my referral link and get 3 free video credits: ${stats?.referral_link}`
    );
    const url = encodeURIComponent(stats?.referral_link || "");
    
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${message}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${message}`,
      whatsapp: `https://wa.me/?text=${message}`,
      email: `mailto:?subject=Create viral videos with AI!&body=${message}`,
    };
    
    window.open(urls[platform], "_blank");
  };

  const updateRewardPreference = async (type) => {
    try {
      await referralAPI.updatePreferences(type);
      setStats(prev => ({ ...prev, reward_preference: type }));
      toast.success(`Reward preference updated to ${type}`);
    } catch (error) {
      toast.error("Failed to update preference");
    }
  };

  const handlePayoutRequest = async () => {
    if (!payoutForm.amount || parseFloat(payoutForm.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    
    if (parseFloat(payoutForm.amount) > (stats?.available_for_payout || 0)) {
      toast.error("Insufficient balance");
      return;
    }
    
    if (payoutForm.method === "paypal" && !payoutForm.paypalEmail) {
      toast.error("Please enter your PayPal email");
      return;
    }
    
    setPayoutLoading(true);
    try {
      await referralAPI.requestPayout(
        parseFloat(payoutForm.amount),
        payoutForm.method,
        payoutForm.paypalEmail || null
      );
      toast.success(
        payoutForm.method === "credits" 
          ? "Credits added to your account!" 
          : "Payout request submitted!"
      );
      setShowPayoutModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Payout request failed");
    } finally {
      setPayoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  const TierBadge = TIER_BADGES[stats?.tier?.tier_id] || TIER_BADGES.bronze;

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="p-6 max-w-7xl mx-auto space-y-6"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="text-center mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">
          Earn While You Create
        </h1>
        <p className="text-white/60">
          Invite friends, get paid. It's that simple.
        </p>
      </motion.div>

      {/* Stats Overview */}
      <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-cyan-500/20 to-purple-500/20 border-cyan-500/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm">Total Referrals</p>
                <p className="text-3xl font-bold text-white">{stats?.total_referrals || 0}</p>
              </div>
              <Users className="w-10 h-10 text-cyan-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-green-500/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm">Total Revenue</p>
                <p className="text-3xl font-bold text-white">£{stats?.total_revenue_generated || 0}</p>
              </div>
              <TrendingUp className="w-10 h-10 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-purple-500/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm">Available to Withdraw</p>
                <p className="text-3xl font-bold text-white">£{stats?.available_for_payout || 0}</p>
              </div>
              <Wallet className="w-10 h-10 text-purple-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 border-amber-500/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm">Credits Earned</p>
                <p className="text-3xl font-bold text-white">{stats?.total_credits_earned || 0}</p>
              </div>
              <Gift className="w-10 h-10 text-amber-400" />
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Referral Link & Share */}
        <motion.div variants={itemVariants} className="lg:col-span-2 space-y-6">
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Share2 className="w-5 h-5 text-cyan-400" />
                Your Referral Link
              </CardTitle>
              <CardDescription>
                Share this link and earn rewards when friends sign up
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={stats?.referral_link || ""}
                  readOnly
                  className="bg-secondary/50 border-border/50 text-white font-mono text-sm"
                />
                <Button
                  onClick={copyReferralLink}
                  className={copied ? "bg-green-500" : "bg-cyan-500 hover:bg-cyan-600"}
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>

              {/* Share Buttons */}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => shareOnSocial("twitter")}
                  className="border-white/20"
                >
                  <Twitter className="w-4 h-4 mr-2 text-sky-400" />
                  Twitter
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => shareOnSocial("facebook")}
                  className="border-white/20"
                >
                  <Facebook className="w-4 h-4 mr-2 text-blue-500" />
                  Facebook
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => shareOnSocial("whatsapp")}
                  className="border-white/20"
                >
                  <MessageCircle className="w-4 h-4 mr-2 text-green-500" />
                  WhatsApp
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => shareOnSocial("email")}
                  className="border-white/20"
                >
                  <Mail className="w-4 h-4 mr-2 text-purple-400" />
                  Email
                </Button>
              </div>

              {/* QR Code */}
              {qrCodeUrl && (
                <div className="flex items-center gap-4 p-4 bg-white rounded-lg w-fit">
                  <img src={qrCodeUrl} alt="Referral QR Code" className="w-32 h-32" />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Earnings Breakdown */}
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-400" />
                Earnings Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-yellow-400" />
                    <span className="text-white/80">Pending (30-day hold)</span>
                  </div>
                  <span className="text-yellow-400 font-bold">£{stats?.pending_commission || 0}</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-400" />
                    <span className="text-white/80">Available to Withdraw</span>
                  </div>
                  <span className="text-green-400 font-bold">£{stats?.available_for_payout || 0}</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-purple-400" />
                    <span className="text-white/80">Total Paid Out</span>
                  </div>
                  <span className="text-purple-400 font-bold">£{stats?.paid_commission || 0}</span>
                </div>

                {(stats?.available_for_payout || 0) >= (stats?.min_payout_amount || 25) && (
                  <Button
                    onClick={() => setShowPayoutModal(true)}
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400"
                  >
                    <Wallet className="w-4 h-4 mr-2" />
                    Request Payout
                  </Button>
                )}
                
                {(stats?.available_for_payout || 0) < (stats?.min_payout_amount || 25) && (stats?.available_for_payout || 0) > 0 && (
                  <p className="text-center text-white/50 text-sm">
                    Minimum payout: £{stats?.min_payout_amount || 25}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Right Column - Tier & Settings */}
        <motion.div variants={itemVariants} className="space-y-6">
          {/* Current Tier */}
          <Card className={`bg-gradient-to-br ${TIER_COLORS[stats?.tier?.tier_id] || TIER_COLORS.bronze} border-0`}>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <TierBadge.icon className={`w-8 h-8 ${TierBadge.color}`} />
                <div>
                  <p className="text-white/80 text-sm">Current Tier</p>
                  <p className="text-2xl font-bold text-white">{stats?.tier?.name || "Bronze"}</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-white/80">Commission Rate</span>
                  <span className="text-white font-bold">{stats?.tier?.commission_percent || 5}%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/80">Credit Reward</span>
                  <span className="text-white font-bold">{stats?.tier?.credit_reward || 5} per referral</span>
                </div>
              </div>

              {stats?.next_tier && (
                <div className="mt-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-white/80">Progress to {stats.next_tier.name}</span>
                    <span className="text-white">{stats.progress_to_next_tier}%</span>
                  </div>
                  <Progress value={stats.progress_to_next_tier} className="h-2" />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Reward Preference */}
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle className="text-lg text-white">Reward Preference</CardTitle>
              <CardDescription>Choose how you want to be rewarded</CardDescription>
            </CardHeader>
            <CardContent>
              <Select
                value={stats?.reward_preference || "commission"}
                onValueChange={updateRewardPreference}
              >
                <SelectTrigger className="bg-secondary/50 border-border/50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="commission">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4" />
                      Cash Commission ({stats?.tier?.commission_percent || 5}%)
                    </div>
                  </SelectItem>
                  <SelectItem value="credits">
                    <div className="flex items-center gap-2">
                      <Gift className="w-4 h-4" />
                      Video Credits ({stats?.tier?.credit_reward || 5} per referral)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Monthly Leaderboard */}
          <Card className="bg-card border-border/50">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-400" />
                Top Referrers
              </CardTitle>
              <CardDescription>This month's leaders</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {leaderboard.slice(0, 5).map((entry, index) => (
                  <div
                    key={entry.user_id}
                    className={`flex items-center gap-3 p-2 rounded-lg ${
                      index === 0 ? "bg-yellow-500/10 border border-yellow-500/30" : ""
                    }`}
                  >
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${
                      index === 0 ? "bg-yellow-500 text-black" :
                      index === 1 ? "bg-slate-400 text-black" :
                      index === 2 ? "bg-amber-700 text-white" :
                      "bg-white/10 text-white/60"
                    }`}>
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm truncate">{entry.name}</p>
                      <p className="text-white/50 text-xs">{entry.referral_count} referrals</p>
                    </div>
                    {index === 0 && (
                      <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">
                        +100 credits
                      </Badge>
                    )}
                  </div>
                ))}
                
                {leaderboard.length === 0 && (
                  <p className="text-white/50 text-sm text-center py-4">
                    Be the first on the leaderboard!
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Referral History */}
      <motion.div variants={itemVariants}>
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg text-white">Referral History</CardTitle>
            <CardDescription>Track your referrals and earnings</CardDescription>
          </CardHeader>
          <CardContent>
            {history.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border/50">
                      <th className="text-left text-white/60 text-sm py-3 px-2">Name</th>
                      <th className="text-left text-white/60 text-sm py-3 px-2">Email</th>
                      <th className="text-left text-white/60 text-sm py-3 px-2">Signed Up</th>
                      <th className="text-left text-white/60 text-sm py-3 px-2">Status</th>
                      <th className="text-left text-white/60 text-sm py-3 px-2">Purchase</th>
                      <th className="text-left text-white/60 text-sm py-3 px-2">Reward</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((ref) => (
                      <tr key={ref.id} className="border-b border-border/30">
                        <td className="py-3 px-2 text-white text-sm font-medium">
                          {ref.referred_user_name || "Unknown"}
                        </td>
                        <td className="py-3 px-2 text-white/60 text-sm">{ref.referred_email_masked}</td>
                        <td className="py-3 px-2 text-white/60 text-sm">
                          {new Date(ref.signup_date).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-2">
                          <Badge variant="outline" className={
                            ref.status === "converted" ? "border-green-500/50 text-green-400" :
                            "border-yellow-500/50 text-yellow-400"
                          }>
                            {ref.status === "converted" ? "Converted" : "Signed Up"}
                          </Badge>
                          {ref.manual_assignment && (
                            <Badge variant="outline" className="ml-2 border-blue-500/50 text-blue-400 text-xs">
                              Manual
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-2 text-white text-sm">
                          {ref.first_purchase_amount ? `£${ref.first_purchase_amount}` : "-"}
                        </td>
                        <td className="py-3 px-2 text-sm">
                          {ref.reward_type === "credits" ? (
                            <span className="text-purple-400">{ref.credit_reward || 0} credits</span>
                          ) : (
                            <span className="text-green-400">£{ref.commission_amount || 0}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-white/20 mx-auto mb-3" />
                <p className="text-white/50">No referrals yet</p>
                <p className="text-white/30 text-sm">Share your link to start earning!</p>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Badges Section */}
      <motion.div variants={itemVariants}>
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Award className="w-5 h-5 text-yellow-400" />
              Achievements & Badges
            </CardTitle>
            <CardDescription>Earn badges and claim rewards as you grow your network</CardDescription>
          </CardHeader>
          <CardContent>
            <BadgesDisplay />
          </CardContent>
        </Card>
      </motion.div>

      {/* Payout Modal */}
      <Dialog open={showPayoutModal} onOpenChange={setShowPayoutModal}>
        <DialogContent className="bg-card border-border/50">
          <DialogHeader>
            <DialogTitle className="text-white">Request Payout</DialogTitle>
            <DialogDescription>
              Withdraw your earnings or convert to credits
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-white">Amount (£)</Label>
              <Input
                type="number"
                value={payoutForm.amount}
                onChange={(e) => setPayoutForm(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="Enter amount"
                max={stats?.available_for_payout || 0}
                className="bg-secondary/50 border-border/50"
              />
              <p className="text-white/50 text-xs mt-1">
                Available: £{stats?.available_for_payout || 0}
              </p>
            </div>
            
            <div>
              <Label className="text-white">Payout Method</Label>
              <Select
                value={payoutForm.method}
                onValueChange={(v) => setPayoutForm(prev => ({ ...prev, method: v }))}
              >
                <SelectTrigger className="bg-secondary/50 border-border/50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="credits">Convert to Video Credits</SelectItem>
                  <SelectItem value="paypal">PayPal</SelectItem>
                  <SelectItem value="stripe">Bank Transfer (Stripe)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {payoutForm.method === "paypal" && (
              <div>
                <Label className="text-white">PayPal Email</Label>
                <Input
                  type="email"
                  value={payoutForm.paypalEmail}
                  onChange={(e) => setPayoutForm(prev => ({ ...prev, paypalEmail: e.target.value }))}
                  placeholder="your@email.com"
                  className="bg-secondary/50 border-border/50"
                />
              </div>
            )}
            
            {payoutForm.method === "credits" && payoutForm.amount && (
              <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
                <p className="text-white/80 text-sm">
                  You'll receive <span className="text-purple-400 font-bold">{Math.floor(parseFloat(payoutForm.amount) || 0)} video credits</span>
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayoutModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handlePayoutRequest}
              disabled={payoutLoading}
              className="bg-gradient-to-r from-green-500 to-emerald-500"
            >
              {payoutLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              {payoutForm.method === "credits" ? "Convert to Credits" : "Request Payout"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
